import {
  pgTable,
  serial,
  integer,
  text,
  timestamp,
  boolean,
  jsonb,
  varchar,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { syncedEmails } from "./emailSync";

// AI-generated suggestions for emails
export const emailAiSuggestions = pgTable("email_ai_suggestions", {
  id: serial("id").primaryKey(),
  syncedEmailId: integer("synced_email_id")
    .notNull()
    .references(() => syncedEmails.id, { onDelete: "cascade" }),

  // Suggestion type: "task" or "calendar_event"
  suggestionType: varchar("suggestion_type", { length: 50 }).notNull(),

  // Suggested content
  title: text("title").notNull(),
  description: text("description"),

  // For tasks/tickets
  priority: varchar("priority", { length: 20 }), // low, medium, high, urgent
  category: varchar("category", { length: 50 }), // benefits_enrollment, plan_changes, etc.

  // For calendar events
  suggestedDate: timestamp("suggested_date"),
  suggestedDuration: integer("suggested_duration"), // in minutes

  // AI confidence and reasoning
  confidence: varchar("confidence", { length: 20 }).notNull(), // high, medium, low
  reasoning: text("reasoning"),

  // Extracted entities from email
  extractedEntities: jsonb("extracted_entities"), // { clientName, employeeName, dates, etc. }

  // User action
  status: varchar("status", { length: 20 }).notNull().default("pending"), // pending, accepted, rejected, dismissed
  acceptedAt: timestamp("accepted_at"),
  rejectedAt: timestamp("rejected_at"),

  // If accepted, reference to created item
  createdTicketId: integer("created_ticket_id"),
  createdAppointmentId: integer("created_appointment_id"),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

// Schema for email analysis results (stored on the synced email)
export const emailAnalysisResults = pgTable("email_analysis_results", {
  id: serial("id").primaryKey(),
  syncedEmailId: integer("synced_email_id")
    .notNull()
    .unique()
    .references(() => syncedEmails.id, { onDelete: "cascade" }),

  // Analysis metadata
  analyzedAt: timestamp("analyzed_at").notNull().defaultNow(),
  modelUsed: varchar("model_used", { length: 100 }),

  // Classification
  emailCategory: varchar("email_category", { length: 50 }), // new_hire, termination, enrollment, billing, compliance, meeting_request, etc.
  urgency: varchar("urgency", { length: 20 }), // low, medium, high, urgent
  requiresAction: boolean("requires_action").default(false),

  // Extracted information
  summary: text("summary"),
  keyPoints: jsonb("key_points"), // array of strings
  extractedDates: jsonb("extracted_dates"), // { mentioned: [], deadlines: [] }
  extractedPeople: jsonb("extracted_people"), // { name, role, company }[]
  extractedCompanies: jsonb("extracted_companies"), // string[]

  // Sentiment
  sentiment: varchar("sentiment", { length: 20 }), // positive, neutral, negative, urgent

  // Raw AI response for debugging
  rawResponse: jsonb("raw_response"),

  createdAt: timestamp("created_at").defaultNow(),
});

// Zod schemas
export const insertEmailAiSuggestionSchema = createInsertSchema(emailAiSuggestions);
export const insertEmailAnalysisResultSchema = createInsertSchema(emailAnalysisResults);

// Types
export type EmailAiSuggestion = typeof emailAiSuggestions.$inferSelect;
export type InsertEmailAiSuggestion = typeof emailAiSuggestions.$inferInsert;
export type EmailAnalysisResult = typeof emailAnalysisResults.$inferSelect;
export type InsertEmailAnalysisResult = typeof emailAnalysisResults.$inferInsert;

// API response types
export interface EmailAnalysisResponse {
  analysis: EmailAnalysisResult;
  suggestions: EmailAiSuggestion[];
}

export interface SuggestionActionRequest {
  suggestionId: number;
  action: "accept" | "reject" | "dismiss";
  // Optional overrides when accepting
  overrides?: {
    title?: string;
    description?: string;
    priority?: string;
    dueDate?: string;
    startTime?: string;
    endTime?: string;
  };
}
